// TaskDetailBody — dispatches a fetched assignment task detail to the right
// per-type view (access approval / resource access), plus the optional audit
// note. Offboarding is handled directly by TaskDrawer (its checklist couples to
// the drawer's pinned Save/Refuse footer), so it is not dispatched here.

import { Box, Flex, Spinner, Text } from "@chakra-ui/react";

import SectionHeader from "components/ds/SectionHeader";
import Textarea from "components/ds/Textarea";
import type {
  MFAccessApprovalEntity,
  MFResourceAccessEntity,
  MFTaskDetail,
  MFTaskItem,
} from "state/members/tasks";

import AccessApprovalView from "./AccessApprovalView";
import EntitlementAdjustmentDetails from "./EntitlementAdjustmentDetails";
import EntitlementTaskDetails from "./EntitlementTaskDetails";
import IndirectAccessRemediationDetails from "./IndirectAccessRemediationDetails";
import ResourceAccessView from "./ResourceAccessView";
import { INDIRECT_REMEDIATION_ENTITY_TYPE, isEntitlementTask } from "./taskMeta";

const Centered = ({ children }: { children: React.ReactNode }) => (
  <Flex align="center" justify="center" py="2xl">
    {children}
  </Flex>
);

// Body-text fallback for tasks with no richer entity view.
const BodyFallback = ({ task }: { task: MFTaskItem }) =>
  task.body ? (
    <Box borderWidth="1px" borderColor="neutral.200" borderRadius="10px" bg="neutral.25" p="lg">
      <Text fontSize="smd" color="neutral.800" whiteSpace="pre-wrap">
        {task.body}
      </Text>
    </Box>
  ) : null;

export interface TaskDetailBodyProps {
  task: MFTaskItem;
  detail?: MFTaskDetail;
  detailLoading: boolean;
  /** Optional note field (audit trail) — shown for actionable assignment tasks. */
  showNote: boolean;
  note: string;
  onNoteChange: (value: string) => void;
}

const TaskDetailBody = ({
  task,
  detail,
  detailLoading,
  showNote,
  note,
  onNoteChange,
}: TaskDetailBodyProps) => {
  if (detailLoading) {
    return (
      <Centered>
        <Spinner size="sm" color="neutral.500" />
      </Centered>
    );
  }

  const entity = detail?.entity;
  const entityType = task.entityType;

  let view: React.ReactNode;
  if (entityType === "access_approval" && entity) {
    view = <AccessApprovalView entity={entity as MFAccessApprovalEntity} />;
  } else if (entityType === "entitlement_adjustment") {
    // Resource card (when the entity loaded) + the flagged entitlements/reason
    // parsed from the task body — the latter is the whole point of this task.
    view = (
      <>
        {entity && (
          <ResourceAccessView
            entity={entity as MFResourceAccessEntity}
            entityType={entityType}
          />
        )}
        <EntitlementAdjustmentDetails body={task.body} />
      </>
    );
  } else if (isEntitlementTask(entityType)) {
    const access = entity as MFResourceAccessEntity | null | undefined;
    view = (
      <>
        {access && <ResourceAccessView entity={access} entityType={entityType} />}
        <EntitlementTaskDetails entityType={entityType} entity={access} />
        <BodyFallback task={task} />
      </>
    );
  } else if (entityType === INDIRECT_REMEDIATION_ENTITY_TYPE) {
    view = (
      <IndirectAccessRemediationDetails
        metadata={detail?.metadata}
        actionable={showNote}
      />
    );
  } else if (
    (entityType === "provisioning" || entityType === "deprovisioning") &&
    entity
  ) {
    view = (
      <ResourceAccessView
        entity={entity as MFResourceAccessEntity}
        entityType={entityType}
      />
    );
  } else {
    view = <BodyFallback task={task} />;
  }

  return (
    <>
      {view}
      {showNote && (
        <Box>
          <SectionHeader>Note (optional)</SectionHeader>
          <Textarea
            placeholder="Add a note that will be saved to the audit trail…"
            value={note}
            onChange={(e) => onNoteChange(e.target.value)}
            aria-label="Task note"
          />
        </Box>
      )}
    </>
  );
};

export default TaskDetailBody;
